import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, ArrowRight, Download, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(response.data);
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (pollCount < 10) {
          // Continue polling
          setPollCount(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('pending');
          setPaymentData(response.data);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount < 3) {
          setPollCount(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50 flex items-center justify-center" data-testid="payment-success-page">
      <div className="w-full max-w-lg px-6 text-center">
        {status === 'checking' && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-slate-900">
              Vérification du paiement...
            </h1>
            <p className="text-slate-600">Veuillez patienter</p>
          </div>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-fade-in">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>

            <h1 className="font-heading font-bold text-3xl text-slate-900 mb-4 animate-fade-in delay-100">
              Paiement réussi !
            </h1>
            
            <p className="text-slate-600 text-lg mb-8 animate-fade-in delay-200">
              Merci pour votre confiance. Votre paiement a été traité avec succès.
            </p>

            {paymentData && (
              <Card className="mb-8 text-left animate-fade-in delay-300">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-lg mb-4">Récapitulatif</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Référence</span>
                      <span className="font-mono text-xs">{sessionId?.substring(0, 20)}...</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Montant</span>
                      <span className="font-bold text-green-600">
                        {(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Statut</span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        Payé
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in delay-400">
              <Link to="/">
                <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 gap-2">
                  Retour à l'accueil
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-amber-500" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-slate-900 mb-4">
              Paiement en cours de traitement
            </h1>
            <p className="text-slate-600 mb-8">
              Votre paiement est en cours de vérification. Vous recevrez une confirmation par email.
            </p>
            <Link to="/">
              <Button variant="outline" className="rounded-full px-8 py-6">
                Retour à l'accueil
              </Button>
            </Link>
          </>
        )}

        {(status === 'error' || status === 'expired') && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="font-heading font-bold text-2xl text-slate-900 mb-4">
              {status === 'expired' ? 'Session expirée' : 'Erreur de vérification'}
            </h1>
            <p className="text-slate-600 mb-8">
              {status === 'expired' 
                ? 'Votre session de paiement a expiré. Veuillez réessayer.'
                : 'Une erreur est survenue. Veuillez réessayer ou nous contacter.'}
            </p>
            <Link to="/siteweb">
              <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6">
                Réessayer
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
